import React from "react";
import { useNavigate } from "react-router-dom";
import { useTheme, fonts } from "@/theme";
import { Icon } from "@/components/Icon";

export function ProgressBar({ percent }) {
  const { colors } = useTheme();
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div style={{ height: 8, borderRadius: 999, background: colors.surfaceTertiary, overflow: "hidden" }}>
      <div
        style={{
          height: "100%",
          width: `${clamped}%`,
          borderRadius: 999,
          background: clamped === 0 ? colors.border : colors.success,
          transition: "width .4s ease",
        }}
      />
    </div>
  );
}

export function SectionTitle({ children, style }) {
  const { colors } = useTheme();
  return (
    <div style={{ color: colors.onSurface, fontSize: 20, fontFamily: fonts.display, fontWeight: 700, ...style }}>
      {children}
    </div>
  );
}

export function PlaceholderTag({ label = "Placeholder" }) {
  const { colors } = useTheme();
  return (
    <span
      data-testid="placeholder-tag"
      style={{
        alignSelf: "flex-start",
        background: colors.brandTertiary,
        color: colors.onBrandTertiary,
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        fontFamily: fonts.text,
        textTransform: "uppercase",
        letterSpacing: 0.5,
      }}
    >
      {label}
    </span>
  );
}

// Full-height page wrapper — mobile-app column centred on wide screens.
export function Screen({ children, maxWidth = 720 }) {
  const { colors } = useTheme();
  return (
    <div style={{ minHeight: "100vh", background: colors.surface }}>
      <div style={{ maxWidth, margin: "0 auto", background: colors.surface, minHeight: "100vh" }}>{children}</div>
    </div>
  );
}

export function TopBar({ title, right, onBack }) {
  const { colors } = useTheme();
  const navigate = useNavigate();
  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 20,
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "16px 20px",
        background: colors.surface,
        borderBottom: `1px solid ${colors.divider}`,
      }}
    >
      <button
        data-testid="back-button"
        className="pressable"
        onClick={() => (onBack ? onBack() : navigate(-1))}
        style={{
          width: 38,
          height: 38,
          borderRadius: 12,
          border: `1px solid ${colors.border}`,
          background: colors.surfaceSecondary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <Icon name="chevron-left" size={22} color={colors.onSurface} />
      </button>
      <div
        style={{
          flex: 1,
          color: colors.onSurface,
          fontSize: 18,
          fontFamily: fonts.display,
          fontWeight: 700,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {title}
      </div>
      {right}
    </div>
  );
}

export function Accordion({ open, onToggle, icon, title, children, testId }) {
  const { colors } = useTheme();
  return (
    <div
      style={{
        background: colors.surfaceSecondary,
        border: `1px solid ${colors.border}`,
        borderRadius: 18,
        marginBottom: 12,
        overflow: "hidden",
      }}
    >
      <button
        data-testid={testId}
        className="pressable"
        onClick={onToggle}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: 16,
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        {icon ? (
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 12,
              background: colors.brandTertiary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon name={icon} size={20} color={colors.onBrandTertiary} />
          </div>
        ) : null}
        <span style={{ flex: 1, color: colors.onSurface, fontSize: 16, fontFamily: fonts.text, fontWeight: 700 }}>
          {title}
        </span>
        <Icon name={open ? "chevron-up" : "chevron-down"} size={20} color={colors.muted} />
      </button>
      {open ? <div style={{ padding: "0 16px 16px" }}>{children}</div> : null}
    </div>
  );
}

export function Loader() {
  const { colors } = useTheme();
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
      <div
        style={{
          width: 34,
          height: 34,
          border: `3px solid ${colors.border}`,
          borderTopColor: colors.brand,
          borderRadius: "50%",
          animation: "ritzyspin 0.8s linear infinite",
        }}
      />
    </div>
  );
}
